import { api } from '../../lib/api';
import { markdownToHtml, markdownTableRows } from './markdown';

const colName = (n) => {
  let name = '';
  let x = n;
  while (x > 0) {
    const m = (x - 1) % 26;
    name = String.fromCharCode(65 + m) + name;
    x = Math.floor((x - 1) / 26);
  }
  return name;
};
const colIndex = (letters) => letters.split('').reduce((acc, ch) => acc * 26 + ch.charCodeAt(0) - 64, 0);

/** Datos de la hoja como texto con letras de columna y números de fila, para que la IA use referencias reales. */
export function sheetDataToText(data) {
  if (!data?.values) return '';
  const start = /^\$?([A-Z]+)\$?(\d+)/.exec(data.used || 'A1');
  const c0 = start ? colIndex(start[1]) : 1;
  const r0 = start ? Number(start[2]) : 1;
  const width = Math.max(1, ...data.values.map((r) => r.length));
  const header = `Fila | ${Array.from({ length: width }, (_, j) => colName(c0 + j)).join(' | ')}`;
  const rows = data.values.map((r, i) => `${r0 + i} | ${r.map((v) => (v === null || v === undefined ? '' : String(v))).join(' | ')}`);
  return `Hoja: ${data.sheet}\nRango con datos: ${data.used}\nCelda activa: ${data.selection}\n${header}\n${rows.join('\n')}`;
}

/** Lee del editor lo que la IA necesita según el tipo de archivo. */
export async function readEditorContext(bridge, kind) {
  // PDF y diagramas: el backend extrae el texto del archivo (no hay plugin puente en esos editores)
  if (kind === 'pdf' || kind === 'diagram') return { context: '' };
  if (kind === 'sheet') {
    const data = await bridge.call('sheetData');
    return { context: sheetDataToText(data), selection: data.selection, target: data.selection };
  }
  if (kind === 'slides') {
    const data = await bridge.call('slidesData');
    const deck = data.slides.map((t, i) => `Diapositiva ${i + 1}:\n${t || '(vacía)'}`).join('\n\n');
    return { context: deck, current: data.slides[data.current] || '', target: `diapositiva ${data.current + 1}` };
  }
  const [context, selection] = await Promise.all([bridge.call('docText'), bridge.call('selectedText')]);
  return { context, selection: (selection || '').trim() };
}

export async function askAuroraAI(payload) {
  const { data } = await api.post('/office/ai', payload, { timeout: 120000 });
  return data;
}

export const errorMessage = (err, fallback = 'Aurora IA no pudo responder') => {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' ? detail : err?.message || fallback;
};

/** Inserta una respuesta de Aurora IA en el editor. mode: 'cursor' | 'append' */
export async function insertAIResult(bridge, result, mode = 'cursor') {
  if (result.type === 'table') return bridge.call('sheetWriteTable', { rows: result.rows });
  if (result.type === 'formula') return bridge.call('sheetSetFormula', { formula: result.formula });
  if (result.type === 'slides') {
    const slides = result.slides.map((s, i) => ({ ...s, cover: !!result.deck && i === 0 }));
    return bridge.call('slidesAdd', { slides }, 60000);
  }
  if (result.type === 'markdown' && result.asTable) {
    return bridge.call('sheetWriteTable', { rows: markdownTableRows(result.text) });
  }
  const html = `<div lang="es-MX">${markdownToHtml(result.text)}</div>`;
  return bridge.call(mode === 'append' ? 'appendHtml' : 'insertHtml', { html });
}

export { markdownToHtml, markdownTableRows };
